import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { AssignRiskAssessmentPayload } from 'api/RiskAssessmentsApi';
import { RiskAssessmentsApi } from 'api/RiskAssessmentsApi';
import { useFiRiskAssessmentCache } from 'tanstack-hooks/fi-risk-assessments/risk-assessments-cache';
import { BANK_CRB_TASKS_KEY, RISK_ASSESSMENTS_KEY } from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';

export function useAssignRiskAssessment() {
  const riskAssessmentsApi = new RiskAssessmentsApi();
  const { userBankId } = useUser();
  const queryClient = useQueryClient();
  const { invalidateAccountRiskProfile, invalidateRiskAssessments } = useFiRiskAssessmentCache();

  const { mutateAsync: assignRiskAssessment, isPending: isAssigningRiskAssessment } = useMutation({
    mutationKey: [RISK_ASSESSMENTS_KEY, 'assign'],
    mutationFn: ({ dispensaryId, ...data }: AssignRiskAssessmentPayload & { dispensaryId: string }) =>
      riskAssessmentsApi.assignRiskAssessment(userBankId, dispensaryId, data),
    onSuccess: (_result, { dispensaryId }) => {
      invalidateRiskAssessments();
      queryClient.invalidateQueries({ queryKey: [BANK_CRB_TASKS_KEY, userBankId, dispensaryId] });
      invalidateAccountRiskProfile(dispensaryId);
    },
    meta: {
      snackbarSuccessMessage: 'Risk assessment sent.',
      snackbarErrorMessage: 'There was an issue sending the risk assessment. Please contact support.'
    }
  });

  return { assignRiskAssessment, isAssigningRiskAssessment };
}
